'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';


interface NavigationItem {
  label: string;
  labelEn: string;
  href: string;
  icon: string;
  priority: number;
}

const MainNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();
  const { user } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();

  const navigationItems: NavigationItem[] = [
  { label: 'الرئيسية', labelEn: 'Home', href: '/homepage', icon: 'HomeIcon', priority: 1 },
  { label: 'من نحن', labelEn: 'About Us', href: '/about-us', icon: 'InformationCircleIcon', priority: 2 },
  { label: 'بطاقات الاشتراك', labelEn: 'Subscription Cards', href: '/subscription-cards', icon: 'CreditCardIcon', priority: 3 },
  { label: 'شبكة الشركاء', labelEn: 'Partners Network', href: '/partners-network', icon: 'BuildingStorefrontIcon', priority: 4 },
  { label: 'اتصل بنا', labelEn: 'Contact Us', href: '/contact-us', icon: 'PhoneIcon', priority: 5 }];


  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMounted]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isActive = (href: string) => pathname === href;

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-1000 transition-premium ${
      isScrolled ? 'bg-card shadow-md' : 'bg-card'}`
      }
      suppressHydrationWarning>

      <nav className="container mx-auto">
        <div className="flex items-center justify-between h-14 lg:h-20 px-4 lg:px-6">
          {/* Logo */}
          <Link
            href="/homepage"
            className="flex items-center gap-2 lg:gap-3 transition-premium hover:opacity-80"
            onClick={closeMobileMenu}>

            <div className="relative w-9 h-9 lg:w-12 lg:h-12 rounded-xl overflow-hidden flex-shrink-0">
              <Image
                src="/assets/images/WhatsApp_Image_2026-07-21_at_11.25.41_AM-1784650017379.jpeg"
                alt="شعار إبداع الحرفة للتسويق"
                fill
                className="object-cover" />

            </div>
            <div className="flex flex-col">
              <span className="text-base lg:text-xl font-bold font-amiri text-primary leading-tight" suppressHydrationWarning>
                إبداع الحرفة للتسويق
              </span>
              <span className="text-xs lg:text-sm text-muted-foreground font-tajawal leading-tight font-bold" suppressHydrationWarning>IBDAA ELHERFA FOR MARKETING 

              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navigationItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              className={`flex items-center gap-2 px-6 py-3 rounded-md transition-premium font-medium ${
              isActive(item.href) ?
              'bg-primary text-primary-foreground shadow-sm' :
              'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                <Icon name={item.icon as any} size={20} />
                <span>{language === 'ar' ? item.label : item.labelEn}</span>
              </Link>
            )}

            {/* Customer Dashboard Link - Only show when logged in */}
            {user &&
            <Link
              href="/customer-dashboard"
              className={`flex items-center gap-2 px-6 py-3 rounded-md transition-premium font-medium ${
              isActive('/customer-dashboard') ?
              'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                <Icon name="UserCircleIcon" size={20} />
                <span>{t('لوحة العضو', 'Member Dashboard')}</span>
              </Link>
            }

            {/* Admin Dashboard Link - Only show for admin */}
            {user?.email === 'haggag@admin.com' &&
            <Link
              href="/admin"
              className={`flex items-center gap-2 px-6 py-3 rounded-md transition-premium font-medium ${
              isActive('/admin') ?
              'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                <Icon name="ShieldCheckIcon" size={20} />
                <span>{t('لوحة الإدارة', 'Admin Panel')}</span>
              </Link>
            }

            {/* Partner Portal Link - Show for logged-in non-admin users */}
            {user && user?.email !== 'haggag@admin.com' &&
            <Link
              href="/partner-portal"
              className={`flex items-center gap-2 px-6 py-3 rounded-md transition-premium font-medium ${
              isActive('/partner-portal') ?
              'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                <Icon name="BuildingStorefrontIcon" size={20} />
                <span>{t('بوابة الشريك', 'Partner Portal')}</span>
              </Link>
            }

            {/* Login Button - Only show when NOT logged in */}
            {!user &&
            <Link
              href="/login"
              className="flex items-center gap-2 px-6 py-3 rounded-md bg-accent text-accent-foreground font-medium transition-premium hover:bg-accent/90 hover:shadow-md active-press mr-2">

                <Icon name="ArrowRightOnRectangleIcon" size={20} />
                <span>{t('تسجيل الدخول', 'Login')}</span>
              </Link>
            }

            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-primary/30 text-primary font-medium transition-premium hover:bg-primary hover:text-primary-foreground ml-2"
              aria-label="Toggle language">

              <Icon name="LanguageIcon" size={18} />
              <span className="text-sm font-bold">{language === 'ar' ? 'EN' : 'ع'}</span>
            </button>
          </div>

          {/* Mobile: Language Toggle + Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Language Toggle - Mobile */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-3 py-2 rounded-md border border-primary/30 text-primary font-medium transition-premium hover:bg-primary hover:text-primary-foreground"
              aria-label="Toggle language">

              <Icon name="LanguageIcon" size={16} />
              <span className="text-sm font-bold">{language === 'ar' ? 'EN' : 'ع'}</span>
            </button>

            <button
              onClick={handleMobileMenuToggle}
              className="p-2 rounded-md text-foreground hover:bg-muted transition-premium focus-ring"
              aria-label={t('القائمة', 'Menu')}>

              <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen &&
        <div className="lg:hidden fixed inset-0 top-16 z-1100 bg-card">
            <div className="flex flex-col p-4 space-y-1 overflow-y-auto h-full">
              {navigationItems.map((item) =>
            <Link
              key={item.href}
              href={item.href}
              onClick={closeMobileMenu}
              className={`flex items-center gap-3 px-4 py-3 rounded-md transition-premium font-medium text-base ${
              isActive(item.href) ?
              'bg-primary text-primary-foreground shadow-sm' :
              'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                  <Icon name={item.icon as any} size={20} />
                  <span>{language === 'ar' ? item.label : item.labelEn}</span>
                </Link>
            )}

              {/* Customer Dashboard Link - Mobile - Only show when logged in */}
              {user &&
            <Link
              href="/customer-dashboard"
              onClick={closeMobileMenu}
              className={`flex items-center gap-3 px-4 py-3 rounded-md transition-premium font-medium text-base ${
              isActive('/customer-dashboard') ?
              'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                  <Icon name="UserCircleIcon" size={20} />
                  <span>{t('لوحة العضو', 'Member Dashboard')}</span>
                </Link>
            }

              {/* Admin Dashboard Link - Mobile - Only show for admin */}
              {user?.email === 'haggag@admin.com' &&
            <Link
              href="/admin"
              onClick={closeMobileMenu}
              className={`flex items-center gap-3 px-4 py-3 rounded-md transition-premium font-medium text-base ${
              isActive('/admin') ?
              'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                  <Icon name="ShieldCheckIcon" size={20} />
                  <span>{t('لوحة الإدارة', 'Admin Panel')}</span>
                </Link>
            }

              {/* Partner Portal Link - Mobile - Show for logged-in non-admin users */}
              {user && user?.email !== 'haggag@admin.com' &&
            <Link
              href="/partner-portal"
              onClick={closeMobileMenu}
              className={`flex items-center gap-3 px-4 py-3 rounded-md transition-premium font-medium text-base ${
              isActive('/partner-portal') ?
              'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted hover:text-primary'}`
              }>

                  <Icon name="BuildingStorefrontIcon" size={20} />
                  <span>{t('بوابة الشريك', 'Partner Portal')}</span>
                </Link>
            }

              {/* Mobile Login Button - Only show when NOT logged in */}
              {!user &&
            <Link
              href="/login"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3 rounded-md bg-accent text-accent-foreground font-medium text-base transition-premium hover:bg-accent/90 hover:shadow-md active-press mt-2">

                  <Icon name="ArrowRightOnRectangleIcon" size={20} />
                  <span>{t('تسجيل الدخول', 'Login')}</span>
                </Link>
            }
            </div>
          </div>
        }
      </nav>
    </header>);

};

export default MainNavigation;